import { type FormEvent, useEffect, useMemo, useState } from 'react'

type AppRole = 'ADMIN' | 'GESTAO'
type AccessStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED'

interface ManagedUser {
  id: string
  username: string
  full_name: string
  role: AppRole
  active: boolean
  archived_at: string | null
  must_change_password: boolean
  status: AccessStatus
  created_at: string
  updated_at: string
  last_sign_in_at: string | null
}

interface UserManagementProps {
  accessToken: string
  currentUserId: string
}

interface ApiError {
  error?: string
}

const roleLabel: Record<AppRole, string> = {
  ADMIN: 'Administrador',
  GESTAO: 'Gestão',
}

function formatDate(value: string | null) {
  if (!value) return 'Nunca'
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

export function UserManagement({ accessToken, currentUserId }: UserManagementProps) {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [resetId, setResetId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [newUser, setNewUser] = useState({
    username: '',
    full_name: '',
    role: 'GESTAO' as AppRole,
    password: '',
  })

  const [editDraft, setEditDraft] = useState({
    full_name: '',
    role: 'GESTAO' as AppRole,
  })

  const [passwordDraft, setPasswordDraft] = useState('')

  async function api(path: string, init?: RequestInit) {
    const result = await fetch(path, {
      ...init,
      headers: {
        authorization: `Bearer ${accessToken}`,
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
    })

    const contentType = result.headers.get('content-type') ?? ''
    if (!contentType.includes('application/json')) {
      throw new Error(
        result.ok
          ? 'O backend administrativo não está disponível neste deploy.'
          : `O backend administrativo respondeu em formato inválido (HTTP ${result.status}).`,
      )
    }

    const payload = await result.json() as ApiError & { users?: ManagedUser[] }
    if (!result.ok) throw new Error(payload.error || 'Operação não concluída.')
    return payload
  }

  async function loadUsers() {
    setLoading(true)
    setError(null)
    try {
      const payload = await api('/api/users')
      setUsers(payload.users ?? [])
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível carregar os acessos.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadUsers()
  }, [accessToken])

  const summary = useMemo(() => ({
    total: users.filter(user => user.status !== 'ARCHIVED').length,
    active: users.filter(user => user.status === 'ACTIVE').length,
    admins: users.filter(user => user.status === 'ACTIVE' && user.role === 'ADMIN').length,
    archived: users.filter(user => user.status === 'ARCHIVED').length,
  }), [users])

  async function createUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusyId('create')
    setError(null)
    setSuccess(null)

    try {
      await api('/api/users', {
        method: 'POST',
        body: JSON.stringify(newUser),
      })
      setNewUser({ username: '', full_name: '', role: 'GESTAO', password: '' })
      setShowCreate(false)
      setSuccess('Acesso criado. A troca da senha inicial será obrigatória no primeiro login.')
      await loadUsers()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível criar o acesso.')
    } finally {
      setBusyId(null)
    }
  }

  function startEdit(user: ManagedUser) {
    setEditingId(user.id)
    setEditDraft({ full_name: user.full_name, role: user.role })
    setResetId(null)
    setError(null)
    setSuccess(null)
  }

  async function saveProfile(user: ManagedUser) {
    setBusyId(user.id)
    setError(null)
    setSuccess(null)
    try {
      await api('/api/users', {
        method: 'PATCH',
        body: JSON.stringify({
          id: user.id,
          operation: 'profile',
          full_name: editDraft.full_name,
          role: editDraft.role,
        }),
      })
      setEditingId(null)
      setSuccess('Perfil atualizado com sucesso.')
      await loadUsers()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível atualizar o perfil.')
    } finally {
      setBusyId(null)
    }
  }

  async function setActive(user: ManagedUser, active: boolean) {
    setBusyId(user.id)
    setError(null)
    setSuccess(null)
    try {
      await api('/api/users', {
        method: 'PATCH',
        body: JSON.stringify({ id: user.id, operation: 'active', active }),
      })
      setSuccess(active ? 'Acesso reativado.' : 'Acesso inativado.')
      await loadUsers()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível alterar a situação do acesso.')
    } finally {
      setBusyId(null)
    }
  }

  async function resetPassword(user: ManagedUser) {
    setBusyId(user.id)
    setError(null)
    setSuccess(null)
    try {
      await api('/api/users', {
        method: 'PATCH',
        body: JSON.stringify({ id: user.id, operation: 'password', password: passwordDraft }),
      })
      setPasswordDraft('')
      setResetId(null)
      setSuccess(`Senha de ${user.full_name} redefinida. A troca será obrigatória no próximo login.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível redefinir a senha.')
    } finally {
      setBusyId(null)
    }
  }

  async function archiveUser(user: ManagedUser) {
    if (!globalThis.confirm(`Arquivar o acesso de ${user.full_name}? O histórico será preservado.`)) return

    setBusyId(user.id)
    setError(null)
    setSuccess(null)
    try {
      await api('/api/users', {
        method: 'DELETE',
        body: JSON.stringify({ id: user.id }),
      })
      setSuccess('Acesso arquivado. O histórico foi preservado.')
      await loadUsers()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível arquivar o acesso.')
    } finally {
      setBusyId(null)
    }
  }

  async function restoreUser(user: ManagedUser) {
    setBusyId(user.id)
    setError(null)
    setSuccess(null)
    try {
      await api('/api/users', {
        method: 'PATCH',
        body: JSON.stringify({ id: user.id, operation: 'restore' }),
      })
      setSuccess('Acesso restaurado.')
      await loadUsers()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível restaurar o acesso.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="user-management" aria-labelledby="users-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Administração</p>
          <h1 id="users-title">Usuários e acessos</h1>
          <p>Crie, ajuste, inative e arquive acessos sem perder a rastreabilidade.</p>
        </div>
        <button
          type="button"
          className="primary-button"
          onClick={() => {
            setShowCreate(value => !value)
            setError(null)
            setSuccess(null)
          }}
        >
          {showCreate ? 'Cancelar novo acesso' : 'Novo acesso'}
        </button>
      </div>

      <dl className="user-summary">
        <div><dt>Acessos</dt><dd>{summary.total}</dd></div>
        <div><dt>Ativos</dt><dd>{summary.active}</dd></div>
        <div><dt>Administradores</dt><dd>{summary.admins}</dd></div>
        <div><dt>Arquivados</dt><dd>{summary.archived}</dd></div>
      </dl>

      {showCreate && (
        <form className="user-create-form" onSubmit={event => void createUser(event)}>
          <div className="user-create-form__heading">
            <strong>Criar acesso</strong>
            <span>O usuário entrará com o identificador abaixo, sem e-mail visível.</span>
          </div>
          <label>
            Usuário
            <input
              required
              autoCapitalize="none"
              autoComplete="off"
              placeholder="nome.sobrenome"
              value={newUser.username}
              onChange={event => setNewUser(current => ({ ...current, username: event.target.value }))}
            />
          </label>
          <label>
            Nome
            <input
              required
              value={newUser.full_name}
              onChange={event => setNewUser(current => ({ ...current, full_name: event.target.value }))}
            />
          </label>
          <label>
            Perfil
            <select
              value={newUser.role}
              onChange={event => setNewUser(current => ({ ...current, role: event.target.value as AppRole }))}
            >
              <option value="GESTAO">Gestão</option>
              <option value="ADMIN">Administrador</option>
            </select>
          </label>
          <label>
            Senha inicial
            <input
              required
              minLength={10}
              type="password"
              autoComplete="new-password"
              value={newUser.password}
              onChange={event => setNewUser(current => ({ ...current, password: event.target.value }))}
            />
          </label>
          <button className="primary-button" disabled={busyId === 'create'} type="submit">
            {busyId === 'create' ? 'Criando…' : 'Criar acesso'}
          </button>
        </form>
      )}

      {error && <p className="form-error" role="alert">{error}</p>}
      {success && <p className="form-success" role="status">{success}</p>}

      <div className="governance-note">
        <strong>Exclusão preserva histórico</strong>
        <span>
          “Excluir acesso” arquiva e bloqueia o login, mas mantém o usuário ligado às importações e auditorias já registradas.
        </span>
      </div>

      {loading ? (
        <p className="directory-loading" role="status">Carregando acessos…</p>
      ) : (
        <div className="user-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Usuário</th>
                <th scope="col">Perfil</th>
                <th scope="col">Situação</th>
                <th scope="col">Último acesso</th>
                <th scope="col"><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const self = user.id === currentUserId
                const editing = editingId === user.id
                const resetting = resetId === user.id
                const busy = busyId === user.id

                return (
                  <tr key={user.id} className={user.status === 'ARCHIVED' ? 'user-row--archived' : undefined}>
                    <th scope="row">
                      {editing ? (
                        <input
                          aria-label={`Nome de ${user.username}`}
                          value={editDraft.full_name}
                          onChange={event => setEditDraft(current => ({ ...current, full_name: event.target.value }))}
                        />
                      ) : (
                        <>
                          <strong>{user.full_name}</strong>
                          <small>@{user.username}{self ? ' · sua conta' : ''}</small>
                        </>
                      )}
                    </th>
                    <td>
                      {editing ? (
                        <select
                          aria-label={`Perfil de ${user.username}`}
                          disabled={self}
                          value={editDraft.role}
                          onChange={event => setEditDraft(current => ({ ...current, role: event.target.value as AppRole }))}
                        >
                          <option value="GESTAO">Gestão</option>
                          <option value="ADMIN">Administrador</option>
                        </select>
                      ) : roleLabel[user.role]}
                    </td>
                    <td>
                      <span className={`status-chip status-chip--${user.status.toLowerCase()}`}>
                        {user.status === 'ACTIVE' ? 'Ativo' : user.status === 'INACTIVE' ? 'Inativo' : 'Arquivado'}
                      </span>
                      {user.must_change_password && user.status === 'ACTIVE' && (
                        <small className="password-status">Troca de senha pendente</small>
                      )}
                    </td>
                    <td>{formatDate(user.last_sign_in_at)}</td>
                    <td>
                      <div className="row-actions">
                        {editing ? (
                          <>
                            <button
                              className="text-button"
                              type="button"
                              disabled={busy}
                              onClick={() => void saveProfile(user)}
                            >
                              Salvar
                            </button>
                            <button className="text-button" type="button" onClick={() => setEditingId(null)}>
                              Cancelar
                            </button>
                          </>
                        ) : resetting ? (
                          <>
                            <input
                              className="password-inline"
                              aria-label={`Nova senha de ${user.username}`}
                              minLength={10}
                              type="password"
                              placeholder="Nova senha"
                              value={passwordDraft}
                              onChange={event => setPasswordDraft(event.target.value)}
                            />
                            <button
                              className="text-button"
                              type="button"
                              disabled={busy || passwordDraft.length < 10}
                              onClick={() => void resetPassword(user)}
                            >
                              Confirmar
                            </button>
                            <button
                              className="text-button"
                              type="button"
                              onClick={() => {
                                setResetId(null)
                                setPasswordDraft('')
                              }}
                            >
                              Cancelar
                            </button>
                          </>
                        ) : user.status === 'ARCHIVED' ? (
                          <button className="text-button" type="button" disabled={busy} onClick={() => void restoreUser(user)}>
                            Restaurar
                          </button>
                        ) : (
                          <>
                            <button className="text-button" type="button" onClick={() => startEdit(user)}>
                              Editar
                            </button>
                            <button
                              className="text-button"
                              type="button"
                              onClick={() => {
                                setResetId(user.id)
                                setEditingId(null)
                                setPasswordDraft('')
                              }}
                            >
                              Redefinir senha
                            </button>
                            {!self && (
                              <>
                                <button
                                  className="text-button"
                                  type="button"
                                  disabled={busy}
                                  onClick={() => void setActive(user, user.status !== 'ACTIVE')}
                                >
                                  {user.status === 'ACTIVE' ? 'Inativar' : 'Reativar'}
                                </button>
                                <button
                                  className="text-button text-button--danger"
                                  type="button"
                                  disabled={busy}
                                  onClick={() => void archiveUser(user)}
                                >
                                  Excluir acesso
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
